import React, { useState } from 'react';
import {
  Send,
  MoreVertical,
  Trash2,
  ChevronDown,
} from 'lucide-react';

interface Admin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  permission: 'admin' | 'full_admin' | 'view_only' | 'pending' | 'removed';
}

const mockAdmins: Admin[] = [
  { id: '1', firstName: 'David', lastName: 'Cohen', email: 'david@school.com', phone: '+972 50-123-4567', permission: 'full_admin' },
  { id: '2', firstName: 'Sarah', lastName: 'Levy', email: 'sarah@school.com', phone: '+972 52-234-5678', permission: 'admin' },
  { id: '3', firstName: 'Michael', lastName: 'Ben-Ari', email: 'michael@school.com', phone: '+972 54-345-6789', permission: 'view_only' },
  { id: '4', firstName: 'Rachel', lastName: 'Green', email: 'rachel@school.com', phone: '+972 53-456-7890', permission: 'pending' },
];

const permissionLabels: Record<string, string> = {
  admin: 'Admin',
  full_admin: 'Full Admin',
  view_only: 'View Only',
  pending: 'Pending',
  removed: 'Removed',
};

const permissionColors: Record<string, string> = {
  admin: 'bg-blue-100 text-blue-700',
  full_admin: 'bg-green-100 text-green-700',
  view_only: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-700',
  removed: 'bg-red-100 text-red-700',
};

export function ManageAdminsSection() {
  const [admins, setAdmins] = useState<Admin[]>(mockAdmins);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'full_admin' | 'view_only'>('admin');
  const [editingPermission, setEditingPermission] = useState<string | null>(null);

  const handleInvite = () => {
    if (!newEmail) return;
    // In real app, send invitation
    console.log('Inviting:', newEmail, 'with role:', newRole);
    setNewEmail('');
  };

  const handlePermissionChange = (adminId: string, permission: Admin['permission']) => {
    setAdmins((prev) =>
      prev.map((admin) =>
        admin.id === adminId ? { ...admin, permission } : admin
      )
    );
    setEditingPermission(null);
  };

  const handleRemove = (adminId: string) => {
    setAdmins((prev) =>
      prev.map((admin) =>
        admin.id === adminId ? { ...admin, permission: 'removed' } : admin
      )
    );
  };

  return (
    <div className="space-y-6">
      {/* Invite Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Invite Your Teammates</h2>
        <p className="text-sm text-gray-500 mb-4">
          Admins can invite additional collaborators who will receive the same permissions as the inviter.
        </p>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="colleague@school.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as typeof newRole)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="admin">Admin</option>
              <option value="full_admin">Full Admin</option>
              <option value="view_only">View Only</option>
            </select>
          </div>
          <div className="pt-6">
            <button
              onClick={handleInvite}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
            >
              <Send className="w-4 h-4" />
              Send
            </button>
          </div>
        </div>
      </div>

      {/* Team List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Manage Your Team</h2>
        <p className="text-sm text-gray-500 mb-4">
          Here you can view and manage access for all 30 day rolling permissions, alerts, files, and access to inside them again.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{admin.firstName}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{admin.lastName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{admin.phone}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{admin.email}</td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => setEditingPermission(editingPermission === admin.id ? null : admin.id)}
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${permissionColors[admin.permission]}`}
                      >
                        {permissionLabels[admin.permission]}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {editingPermission === admin.id && (
                        <div className="absolute top-full left-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          {(['admin', 'full_admin', 'view_only', 'pending', 'removed'] as const).map((perm) => (
                            <button
                              key={perm}
                              onClick={() => handlePermissionChange(admin.id, perm)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                                admin.permission === perm ? 'text-teal-600 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {permissionLabels[perm]}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleRemove(admin.id)}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
